import { useMemo, useState } from 'react'
import { Card } from '../ui/Card'
import { cn } from '@/lib/utils'
import { tv } from 'tailwind-variants'
import { optimisticPercentage, formatOptimisticPercentage } from '@/utils/math/percentage'
import type { Survey } from '@/data-access/surveys/types'
import { testIdFactory } from '@/utils/test-id-factory.ts'
import Check from '@/assets/icons/Check.svg?react'
import Lock from '@/assets/icons/Lock.svg?react'
import { formatToHumanReadableNumber } from '@/utils/human-readable-number'
import { formatEther } from 'viem'
import { usePollMetrics } from './usePollMetrics'

export interface Option {
	description: string
	value: string
	/** Icon URL or Functional Component */
	icon: string | (({ className }: { className?: string }) => JSX.Element)
	votes: bigint
	tokens: bigint
}

interface RenderProps<T> {
	selectedOption: T | undefined
	isDisabled: boolean
	isReadonly: boolean
}

export interface PollProps extends Pick<Survey, 'question' | 'endDate' | 'startDate'> {
	options: Option[]
	selectedOption?: string
	inactive?: boolean
	readonly?: boolean
	className?: string
	children?: React.ReactNode | ((props: RenderProps<string>) => React.ReactNode)
	testIdDiscriminator: string
}

const optionStyles = tv({
	base: 'grid grid-cols-[auto_1fr] md:grid-cols-[auto_auto_1fr] gap-x-3 gap-y-1 md:gap-y-0 items-center grid-rows-2 md:grid-rows-1 border-2 border-neutral-100 py-2 px-4 rounded-full relative isolate w-full overflow-hidden',
	variants: {
		isSelected: {
			true: 'border-danger'
		},
		isInactive: {
			true: 'opacity-40'
		},
		isReadonly: {
			true: 'pointer-events-none'
		},
		isUpcoming: {
			true: 'grid-rows-1'
		}
	},
	compoundVariants: [
		{
			isSelected: true,
			isInactive: true,
			className: 'border-danger bg-danger/30'
		},
		{
			isSelected: true,
			isReadonly: true,
			className: 'border-danger/50'
		}
	]
})

export function PollCard(props: PollProps) {
	const { question, options, endDate, startDate, inactive, readonly, className, children } = props

	const [selectedOption, setSelectedOption] = useState<string | undefined>(props.selectedOption)
	const hasStarted = startDate ? new Date() > startDate : true
	const hasEnded = endDate ? new Date() > endDate : false
	const isUpcoming = !hasStarted && !hasEnded

	const { totalTokensUsed, hasLockedTokens, totalVotes } = usePollMetrics(options)

	const onClickOption = (option: Option) => {
		setSelectedOption(option.value)
	}

	return (
		<Card className={cn('leading-none w-full relative', className)}>
			<div className="py-6 px-4 md:p-10">
				<h1 className="text-[2rem] mb-5 uppercase text-center">{question}</h1>
				<div className="flex flex-col gap-5 mb-10">
					{options.map((option, idx) => (
						<button
							disabled={inactive || hasEnded || !hasStarted}
							key={idx}
							className={optionStyles({
								isSelected: inactive
									? props.selectedOption === option.description
									: selectedOption === option.value,
								isInactive: inactive || hasEnded,
								isReadonly: readonly || hasEnded || !hasStarted,
								isUpcoming
							})}
							onClick={() => onClickOption(option)}
							data-testid={testIdFactory.pepperPollOptionButton(props.testIdDiscriminator, idx)}
						>
							<div
								className={cn('absolute inset-0 bg-neutral-100/10 rounded-l-full -z-10', {
									'bg-danger/30': selectedOption === option.value
								})}
								style={{
									width: `${optimisticPercentage(Number(option.tokens), Number(totalTokensUsed))}%`
								}}
							/>
							<div className="row-span-2 self-center">
								{typeof option.icon === 'string' ? (
									<img
										src={option.icon}
										alt={option.description}
										width={120}
										height={120}
										className="size-[2.75rem] md:size-[3.75rem]"
										loading="lazy"
									/>
								) : (
									option.icon({ className: 'size-[2.75rem] md:size-[3.75rem]' })
								)}
							</div>
							<span className="text-[1.3rem] md:text-2xl text-left leading-none md:leading-normal col-start-2">
								{option.description}
							</span>
							{!isUpcoming && (
								<div className="text-[1.3rem] md:text-2xl flex gap-2 items-center text-green md:ml-auto">
									<span>
										{formatOptimisticPercentage(Number(option.tokens), Number(totalTokensUsed))}
									</span>
									<div className="flex gap-1 items-center">
										<Check className="size-[20px]" />
										<span>{Number(option.votes)}</span>
									</div>
									<div className="flex gap-1 items-center">
										<Lock className="size-[20px]" />
										<span>
											{hasLockedTokens
												? `${formatToHumanReadableNumber(formatEther(option.tokens), 1)}+`
												: 0}
										</span>
									</div>
								</div>
							)}
						</button>
					))}
				</div>
				{typeof children === 'function'
					? children({
							selectedOption,
							isDisabled: !!inactive,
							isReadonly: !!readonly || hasEnded || !hasStarted
						})
					: children}
			</div>
			{!isUpcoming && (
				<div className="bg-green-20 text-[28px] md:text-[32px] text-green py-5 rounded-b-4xl flex flex-col md:flex-row gap-5 items-center justify-center leading-none">
					<span className="flex gap-2 items-center">
						<Check className="size-[24px]" />
						<span>{`Total Voters: ${Number(totalVotes)}`}</span>
					</span>
					<span className="hidden md:block">//</span>
					<span className="flex gap-2 items-center">
						<Lock className="size-[24px]" />
						<span>{`Total PEPPER Locked: ${hasLockedTokens ? `${formatToHumanReadableNumber(formatEther(totalTokensUsed), 1)}+` : 0}`}</span>
					</span>
				</div>
			)}
		</Card>
	)
}
